/**
 * BanksPage — danh sách bank ref + CRUD inline dialog.
 */
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { HIDE_ON_MOBILE_INLINE } from '@/theme/responsive'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import ConfirmDialog from '@/components/ConfirmDialog'
import DataTableMUI from '@/components/DataTableMUI'
import FormDialog from '@/components/FormDialog'
import PageDataState from '@/components/PageDataState'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useCreateBankMutation,
  useDeleteBankMutation,
  useListBanksQuery,
  useUpdateBankMutation,
} from '@/store/api/purchaseContractApi'
import { useDebounce } from '@/hooks/useDebounce'
import { useSnackbar } from '@/hooks/useSnackbar'
import { getErrorMessage } from '@/utils/error'
import type { Bank } from '@/types/api'

interface BankFormValues {
  bank_code: string
  bank_name: string
  bank_name_eng: string
}

const EMPTY: BankFormValues = { bank_code: '', bank_name: '', bank_name_eng: '' }

const schema: yup.ObjectSchema<BankFormValues> = yup.object({
  bank_code: yup.string().trim().required('Code is required').max(20),
  bank_name: yup.string().trim().required('Name is required').max(200),
  bank_name_eng: yup.string().trim().max(200).default(''),
})

export function BanksPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 })

  const [editing, setEditing] = useState<Bank | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<Bank | null>(null)

  const { data, isLoading, error, isFetching } = useListBanksQuery({
    q: debouncedSearch || undefined,
    page: pagination.page + 1,
    page_size: pagination.pageSize,
  })

  const [createBank, { isLoading: creating }] = useCreateBankMutation()
  const [updateBank, { isLoading: updating }] = useUpdateBankMutation()
  const [deleteBank, { isLoading: deleting }] = useDeleteBankMutation()

  const dialogOpen = createOpen || !!editing
  const isEdit = !!editing
  const dialogLoading = creating || updating

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BankFormValues>({ resolver: yupResolver(schema), defaultValues: EMPTY })

  useEffect(() => {
    if (!dialogOpen) return
    if (isEdit && editing) {
      reset({
        bank_code: editing.bank_code,
        bank_name: editing.bank_name ?? '',
        bank_name_eng: editing.bank_name_eng ?? '',
      })
    } else {
      reset(EMPTY)
    }
  }, [dialogOpen, isEdit, editing, reset])

  const rows = useMemo(
    () => (data?.items ?? []).map((b) => ({ id: b.bank_code, ...b })),
    [data],
  )

  const columns = useMemo<GridColDef<Bank & { id: string }>[]>(
    () => [
      { field: 'bank_code', headerName: 'Code', width: 140 },
      { field: 'bank_name', headerName: 'Bank Name', flex: 1, minWidth: 240 },
      { field: 'bank_name_eng', headerName: 'English Name', flex: 1, minWidth: 220 },
      {
        field: '_actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                disabled={!canEdit}
                onClick={(e) => {
                  e.stopPropagation()
                  setEditing(params.row)
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                disabled={!canEdit}
                onClick={(e) => {
                  e.stopPropagation()
                  setConfirmDelete(params.row)
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  )

  const handleCloseDialog = () => {
    setCreateOpen(false)
    setEditing(null)
  }

  const onSubmit = handleSubmit(async (values) => {
    const payload: Bank = {
      bank_code: values.bank_code.trim(),
      bank_name: values.bank_name.trim(),
      bank_name_eng: values.bank_name_eng || null,
    }
    try {
      if (isEdit && editing) {
        await updateBank({ bank_code: editing.bank_code, body: payload }).unwrap()
        snack.success(`Updated bank ${editing.bank_code}`)
      } else {
        await createBank(payload).unwrap()
        snack.success(`Created bank ${payload.bank_code}`)
      }
      handleCloseDialog()
    } catch (err) {
      snack.error(getErrorMessage(err, isEdit ? 'Update failed' : 'Failed to create bank'))
    }
  })

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteBank(confirmDelete.bank_code).unwrap()
      snack.success(`Deleted bank ${confirmDelete.bank_code}`)
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to delete bank'))
    }
  }

  const isEmpty = !isLoading && !error && (data?.items?.length ?? 0) === 0

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700}>
          Banks
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          disabled={!canEdit}
          onClick={() => setCreateOpen(true)}
          title="New Bank"
        >
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New Bank</Box>
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            label="Search"
            placeholder="Code, name..."
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPagination((p) => ({ ...p, page: 0 }))
            }}
            sx={{ minWidth: 260 }}
          />
        </Box>
      </Paper>

      <PageDataState loading={isLoading} error={error} empty={isEmpty}>
        <Paper>
          <DataTableMUI
            rows={rows}
            columns={columns as GridColDef[]}
            loading={isFetching}
            paginationMode="server"
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            rowCount={data?.total ?? 0}
            onRowClick={(p) => setEditing(p.row as Bank)}
          />
        </Paper>
      </PageDataState>

      <FormDialog
        open={dialogOpen}
        title={isEdit ? 'Edit Bank' : 'New Bank'}
        loading={dialogLoading}
        onClose={handleCloseDialog}
        onSubmit={() => void onSubmit()}
      >
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField
            label="Bank Code *"
            size="small"
            fullWidth
            {...register('bank_code')}
            error={!!errors.bank_code}
            helperText={errors.bank_code?.message}
            disabled={isEdit}
          />
          <TextField
            label="Bank Name *"
            size="small"
            fullWidth
            {...register('bank_name')}
            error={!!errors.bank_name}
            helperText={errors.bank_name?.message}
          />
          <TextField
            label="English Name"
            size="small"
            fullWidth
            {...register('bank_name_eng')}
            error={!!errors.bank_name_eng}
            helperText={errors.bank_name_eng?.message}
          />
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete bank"
        message={
          confirmDelete
            ? `Are you sure you want to delete bank "${confirmDelete.bank_code} — ${confirmDelete.bank_name}"?`
            : ''
        }
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  )
}

export default BanksPage
